/** Words and shapes for the Coupang programme, shared by its page, the
 *  campaign form and the review card. */

import type { Campaign, CoupangDraftMeta, CoupangSignalKind, Draft } from '@/api/client'
import type { Tone } from '@/lib/status'

/** A signal: the moment a product became worth a post. */
export const SIGNAL_KIND: Record<CoupangSignalKind, { label: string; tone: Tone }> = {
  price_drop: { label: '가격 하락', tone: 'scheduled' },
  high_discount: { label: '높은 할인', tone: 'review' },
  new_bestseller: { label: '새 베스트', tone: 'web' },
  lowest_price: { label: '최저가', tone: 'published' },
}

export const SIGNAL_KINDS = Object.keys(SIGNAL_KIND) as CoupangSignalKind[]

export function signalLabel(kind: string): string {
  return SIGNAL_KIND[kind as CoupangSignalKind]?.label ?? kind
}

export function signalTone(kind: string): Tone {
  return SIGNAL_KIND[kind as CoupangSignalKind]?.tone ?? 'neutral'
}

/** Post tones: how the partner link reaches the reader. */
export const AFFILIATE_TONES = [
  { value: 'promo', label: '홍보형', note: '본문에 링크를 넣는다' },
  { value: 'review', label: '후기형', note: '본문은 후기만, 링크는 답글로 단다' },
] as const

export function toneLabel(tone: string): string {
  return AFFILIATE_TONES.find((t) => t.value === tone)?.label ?? tone
}

/** 19,900원 */
export function won(n: number | null | undefined): string {
  return n == null ? '—' : `${n.toLocaleString('ko-KR')}원`
}

/** The meta of a Coupang draft, or null for any other draft. */
export function coupangMetaOf(d: { PlatformMeta?: string }): CoupangDraftMeta | null {
  try {
    const v = JSON.parse(d.PlatformMeta || '{}')
    return v && typeof v === 'object' && v.kind === 'coupang' ? (v as CoupangDraftMeta) : null
  } catch {
    return null
  }
}

/** The poll intervals the page offers, in seconds. */
export const POLL_INTERVALS: { seconds: number; label: string }[] = [
  { seconds: 1800, label: '30분' },
  { seconds: 3600, label: '1시간' },
  { seconds: 10800, label: '3시간' },
  { seconds: 21600, label: '6시간' },
  { seconds: 43200, label: '12시간' },
  { seconds: 86400, label: '하루' },
]

export function intervalLabel(seconds: number): string {
  const hit = POLL_INTERVALS.find((i) => i.seconds === seconds)
  if (hit) return hit.label
  if (seconds % 3600 === 0) return `${seconds / 3600}시간`
  return `${Math.round(seconds / 60)}분`
}

/** How often the server's auto-approve tick runs (internal/app/coupang_wiring.go). */
export const AUTO_APPROVE_EVERY_MS = 10 * 60_000

/** Whether the auto-approve tick will take this draft on its next pass, by the
 *  same rules the server uses (internal/affiliate/autoapprove.go): a draft
 *  waiting in review, in an active campaign with auto-approve on, that a
 *  person has not pulled back after it was auto-approved once. A Coupang
 *  campaign reads its own switch and takes only Coupang drafts. */
export function autoApproves(d: Draft, campaign: Campaign | undefined): boolean {
  if (d.Status !== 'pending_review' || d.ApprovedBy === 'auto' || campaign?.Status !== 'active') return false
  if (campaign.Mode === 'affiliate') return !!coupangMetaOf(d)?.detected_at && !!campaign.Affiliate?.auto_approve
  return !!campaign.AutoApprove
}

/** When the next tick comes, from the last one's start. Past its time it reads
 *  as soon (the tick may be running, or the last run not refetched yet); two
 *  periods without a run reads as stalled. */
export type AutoApproveEta =
  | { kind: 'in'; minutes: number }
  | { kind: 'soon' }
  | { kind: 'stalled'; minutes: number }
  | { kind: 'unknown' }

export function autoApproveEta(lastRunAt: string | null | undefined, now: number): AutoApproveEta {
  const last = lastRunAt ? Date.parse(lastRunAt) : NaN
  if (Number.isNaN(last)) return { kind: 'unknown' }
  const left = last + AUTO_APPROVE_EVERY_MS - now
  if (left > 0) return { kind: 'in', minutes: Math.ceil(left / 60_000) }
  if (now - last < 2 * AUTO_APPROVE_EVERY_MS) return { kind: 'soon' }
  return { kind: 'stalled', minutes: Math.floor((now - last) / 60_000) }
}
